import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {ProductSeatUpdateItemActionStatus} from 'src/openapi/admin/models/ProductSeatUpdateItem';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import type {GetProgress, GetResult} from 'src/components/bulk-tool/typedef';
import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ProductSeatUpdateItem} from 'src/openapi/admin/models/ProductSeatUpdateItem';

type Props = {
    id: string,
    onClose: () => void,
    toolTitle: string,
    fileName: string,
    fileHeaders: Record<string, string>,
    getProgress: GetProgress,
    getResult: GetResult<ProductSeatUpdateItem, void>,
};

export const ProductSeatUpdateResultModal = ({
    id,
    onClose,
    toolTitle,
    fileName,
    fileHeaders,
    getProgress,
    getResult,
}: Props) => {
    const rows = useCallback((items: ProductSeatUpdateItem[], pagination: Pagination) =>
        items.slice((pagination.currentPage - 1) * pagination.pageSize, pagination.currentPage * pagination.pageSize)
            .map(item => (
                <Row key={`${item.userId}:${item.productAccessTermId}`}>
                    <Cell>
                        <UserLink
                            userId={item.userId}
                            caption={item.userEmail}
                            blank={true}
                            place={`Bulk ${toolTitle} Result`}
                        />
                    </Cell>
                    <Cell>
                        <Group direction="h" gap="s">
                            <strong>{item.productName}</strong>
                            <span>{item.startDate} - {item.endDate}</span>
                        </Group>
                    </Cell>
                    <Cell><CompleteMark errors={item.errors}/></Cell>
                </Row>
            )), [toolTitle]);

    return (
        <BulkActionResultModal<ProductSeatUpdateItem>
            id={id}
            onClose={onClose}
            toolTitle={toolTitle}
            description={result => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={toolTitle}
                        fileName={fileName}
                        headers={fileHeaders}
                        startTime={result.startTime}
                        body={(result.itemList ?? []).map(item => ({
                            userEmail: item.userEmail,
                            productName: item.productName,
                            externalSubscriptionId: item.externalSubscriptionId,
                            startDate: item.startDate || '',
                            endDate: item.endDate || '',
                            actionStatus: ProductSeatUpdateItemActionStatus.FAILED === item.actionStatus
                                ? (item.errors?.join('. ') || 'System error')
                                : item.actionStatus,
                        }))}
                    />
                </Group>
            )}
            table={(items, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={200}>Email</Cell>
                        <Cell>Product Access Term</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>{rows(items, pagination)}</Body>
                </Table>
            )}
            getProgress={getProgress}
            getResult={getResult}
            sortComparator={(left, right) => left.userEmail.localeCompare(right.userEmail)
                || left.productName.localeCompare(right.productName)
                || left.externalSubscriptionId.localeCompare(right.externalSubscriptionId)
                || left.productAccessTermId.localeCompare(right.productAccessTermId)}
        />
    );
};
